import classnames from 'classnames';
import Button from 'components/helpers/Button';
import { Sitecore } from '.generated/templates/_.Sitecore.Override';
import { Routes } from '.generated/templates/Project.Routes.model';
import { Data } from '.generated/templates/Feature.Data.model';
import useDictionary from 'lib/sitecore/use-dictionary';
import { DateTime } from 'luxon';
import getThemeClasses from 'lib/util/theming';
import { undefinedDate } from 'lib/util/event';
import ImageWrapper from '../ImageWrapper/ImageWrapper';
import { PageCardProps, getFeaturedImageCaption, getLinkAndHeaderProps } from './card-utils';
import VideoItem, { VideoItemProps } from 'components/helpers/VideoItem/VideoItem';
import { ImageField, useSitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import CardHeading from './cardHeading';

export type ArticlePage = Sitecore.Override.ItemEx & Routes.Fields.ArticlePage;

const ArticlePageCard = (props: PageCardProps): React.JSX.Element => {
  const { sitecoreContext } = useSitecoreContext();
  const dictionary = useDictionary();

  const { promotedPage, isSingleCard, onGtmPush, pageCard } = props;
  if (!promotedPage) {
    return <></>;
  }
  const articlePage = promotedPage as ArticlePage & {
    fields: {
      author?: Data.Authors.Fields.Author;
      pageFeaturedVideo?: VideoItemProps;
    };
  };

  const featuredImage = articlePage.fields.pageFeaturedImage.value?.src
    ? articlePage.fields.pageFeaturedImage
    : (sitecoreContext.defaultFeaturedImage as ImageField);

  const imageCaption = getFeaturedImageCaption(promotedPage.fields.featuredImageCaption);

  const searchPageUrl = sitecoreContext.searchPageUrl || '/search';

  const prefix = articlePage.fields.author?.fields.prefix.value
    ? articlePage.fields.author?.fields.prefix.value.concat(' ')
    : '';

  const firstName = articlePage.fields.author?.fields.firstName.value
    ? articlePage.fields.author?.fields.firstName.value
    : '';

  const lastName = articlePage.fields.author?.fields.lastName.value
    ? ' '.concat(articlePage.fields.author?.fields.lastName.value)
    : '';

  const suffix = articlePage.fields.author?.fields.suffix.value
    ? ' '.concat(articlePage.fields.author?.fields.suffix.value)
    : '';

  const title = articlePage.fields.author?.fields.title.value
    ? ', '.concat(articlePage.fields.author?.fields.title.value)
    : '';

  let description;
  if (pageCard?.fields?.descriptionOverride?.value) {
    description = pageCard?.fields?.descriptionOverride.value;
  } else {
    description = articlePage.fields.pageDescription.value;
  }

  const fullAuthor = `${prefix}${firstName}${lastName}${suffix}${title}`;

  const authorSearchPageUrl = searchPageUrl
    .concat('?type=article&author=')
    .concat(firstName)
    .concat(lastName);

  const { linkField, headingProps } = getLinkAndHeaderProps(props, false);
  const by = dictionary.By;

  let formattedPublishedDate = '';
  if (
    articlePage.fields.publishedDate?.value &&
    articlePage.fields.publishedDate.value !== undefinedDate
  ) {
    const publishedDateTime = DateTime.fromISO(articlePage.fields?.publishedDate.value);
    formattedPublishedDate = publishedDateTime.toLocaleString(DateTime.DATE_MED);
  }

  return (
    <>
      <div className={classnames('callout-card-img')}>
        {!articlePage.fields.pageFeaturedVideo ? (
          <ImageWrapper field={featuredImage} layout="responsive" />
        ) : (
          <div
            className={classnames(
              'col-span-2',
              'md:col-span-1',
              'lg:col-span-2',
              'md:grid-cols-2',
              'lg:grid-cols-3'
            )}
          >
            <VideoItem {...articlePage.fields.pageFeaturedVideo} hideVideoContent />
          </div>
        )}
      </div>
      <div
        className={classnames('callout-card-content', 'max-h-full', getThemeClasses('theme-white'))}
      >
        <CardHeading
          headingProps={headingProps}
          cardEyebrow={formattedPublishedDate}
          imageCaption={imageCaption}
          isSingleCard={isSingleCard}
          descriptionDisabled
        />
        {articlePage.fields.author?.fields.firstName ? (
          <div className={classnames('temp-author', 'mb-3', 'text-gray-darkGray')}>
            {by}{' '}
            <a href={authorSearchPageUrl} className={classnames('text-blue', 'font-semibold')}>
              {fullAuthor}
            </a>
          </div>
        ) : (
          <></>
        )}
        {description && <div className={classnames('temp-desc', 'mb-4')}>{description}</div>}
        <div className={classnames('cta-container')}>
          <Button
            linkField={linkField}
            className={classnames('mr-2', 'sm:mr-4')}
            onGtmPush={onGtmPush}
          />
        </div>
      </div>
    </>
  );
};

export default ArticlePageCard;
